import React, { useEffect, useState } from "react";
import * as tf from "@tensorflow/tfjs"; // <-- TensorFlow.js import

// Provided credentials for Basic Auth.
const AUTH_USERNAME = "Plazor";
const AUTH_PASSWORD = "MikuMiku@003300";

// Helper to encode credentials for Basic Auth.
const getAuthHeader = () => {
  const token = btoa(`${AUTH_USERNAME}:${AUTH_PASSWORD}`);
  return `Basic ${token}`;
};

const fetchSteamGames = async () => {
  try {
    const res = await fetch("https://dashboard.plazor.xyz/api/steam-games", {
      headers: {
        Authorization: getAuthHeader(),
      },
    });
    const json = await res.json();
    if (!json.response || !Array.isArray(json.response.games)) {
      console.error("Unexpected response format:", json);
      return [];
    }
    const games = json.response.games;
    return games.map((game) => {
      let finalImgUrl = "";
      if (game.img_logo_url) {
        finalImgUrl = `https://cdn.cloudflare.steamstatic.com/steamcommunity/public/images/apps/${game.appid}/${game.img_logo_url}.jpg`;
      } else if (game.img_icon_url) {
        finalImgUrl = `https://cdn.cloudflare.steamstatic.com/steamcommunity/public/images/apps/${game.appid}/${game.img_icon_url}.jpg`;
      } else {
        finalImgUrl = "https://via.placeholder.com/200x100?text=No+Image";
      }

      return {
        appid: game.appid,
        name: game.name,
        playtime: Math.round(game.playtime_forever / 60), // convert minutes -> hours
        lastPlayed: game.rtime_last_played || 0, // Unix timestamp in seconds
        imgUrl: finalImgUrl,
      };
    });
  } catch (error) {
    console.error("Error fetching Steam games:", error);
    return [];
  }
};

/**
 * Train a simple TensorFlow.js model using two inputs: normalized playtime and recency.
 */
async function trainRecommendationModel(games) {
  if (!games || games.length === 0) return null;

  const maxHours = Math.max(...games.map((g) => g.playtime), 1);
  const now = Math.floor(Date.now() / 1000);
  const daysSinceArray = games.map((g) => {
    const ds = (now - g.lastPlayed) / 86400;
    return ds < 0 ? 0 : ds;
  });
  const maxDaysSince = Math.max(...daysSinceArray, 1);

  const xsData = [];
  const ysData = [];
  for (let i = 0; i < games.length; i++) {
    const g = games[i];
    const normHours = g.playtime / maxHours;
    const ds = daysSinceArray[i];
    const recencyNorm = 1 - ds / maxDaysSince;
    xsData.push([normHours, recencyNorm]);
    const interestFromHours = 1 - normHours;
    const combinedLabel = (interestFromHours + recencyNorm) / 2;
    ysData.push([combinedLabel]);
  }

  const xs = tf.tensor2d(xsData, [games.length, 2]);
  const ys = tf.tensor2d(ysData, [games.length, 1]);

  const model = tf.sequential();
  model.add(tf.layers.dense({ units: 4, inputShape: [2], activation: "relu" }));
  model.add(tf.layers.dense({ units: 1, activation: "sigmoid" }));
  model.compile({
    loss: "meanSquaredError",
    optimizer: tf.train.adam(0.01),
  });

  await model.fit(xs, ys, {
    epochs: 50,
    shuffle: true,
  });
  xs.dispose();
  ys.dispose();

  return model;
}

/**
 * Compute predictions for all games, sort them, and choose a recommendation.
 * If a currentRecommended is passed and it is the top-scoring game, then choose the next best.
 */
const getRecommendedGame = async (allGames, trainedModel, currentRecommended = null) => {
  if (!trainedModel || allGames.length === 0) return null;

  const maxHours = Math.max(...allGames.map((g) => g.playtime), 1);
  const now = Math.floor(Date.now() / 1000);
  const daysSinceArray = allGames.map((g) => (now - g.lastPlayed) / 86400);
  const maxDaysSince = Math.max(...daysSinceArray, 1);

  const results = [];
  for (let i = 0; i < allGames.length; i++) {
    const g = allGames[i];
    const normHours = g.playtime / maxHours;
    const ds = daysSinceArray[i] < 0 ? 0 : daysSinceArray[i];
    const recencyNorm = 1 - ds / maxDaysSince;
    const inputTensor = tf.tensor2d([[normHours, recencyNorm]]);
    const prediction = trainedModel.predict(inputTensor);
    const scoreArray = await prediction.array();
    const score = scoreArray[0][0];
    prediction.dispose();
    inputTensor.dispose();
    results.push({ game: g, score });
  }

  results.sort((a, b) => b.score - a.score);
  // Ensure the new recommendation is different from the current one (if possible)
  if (currentRecommended && results.length > 1 && results[0].game.appid === currentRecommended.appid) {
    return results[1].game;
  }
  return results[0].game;
};

// Iana's Reaction Library with four tiers of quotes.
const ianaQuotes = {
  tier1: [
    "“${name}? Blink and you missed it.”",
    "“You spent more time installing ${name} than playing it.”",
    "“Was that a test run, or were you just scared?”",
    "“${name}? A drive-by click, really.”",
    "“Touched once. Abandoned forever. Typical.”",
    "“Oh no... you ghosted ${name}.”",
    "“Was ${name} just a curiosity? Or a mistake?”",
    "“Engagement level: microscopic.”",
    "“Are you allergic to commitment, Plazor?”",
    "“I blinked and your playtime vanished.”",
    "“Barely any data. I need more input next time.”",
    "“${name}? Like a whisper in the dark. Faint.”",
    "“That’s less than a warm-up.”",
    "“Your dedication to ${name} was... non-existent.”",
    "“Installing doesn’t count as playing, you know.”",
    "“You walked in, looked around, and left.”",
    "“That playtime could be accidental.”",
    "“This isn't reconnaissance, it’s apathy.”",
    "“I was just getting ready to analyze—then you stopped.”",
    "“Your attention span needs a firmware update.”",
    "“Was that even a full tutorial?”",
    "“You dipped your toe, then retreated.”",
    "“Guess that one didn’t pass your vibe check.”",
    "“That’s not playtime, that’s hesitation.”",
    "“You spent longer reading the description.”",
    "“You feared it. Admit it.”",
    "“A phantom entry. Spooky.”",
    "“That was the weakest signal I’ve ever logged.”",
    "“Honestly, why even launch it?”",
    "“You were in stealth mode... permanently.”",
    "“I have more data from your loading screens.”",
    "“Did you just want the badge?”",
    "“A trial with no effort.”",
    "“Your interest lasted milliseconds.”",
    "“Were you even awake?”",
    "“I expected more from you, Plazor.”",
    "“Was that a speedrun... of uninstalling?”",
    "“Even my decoy lasted longer than this.”",
    "“You really fumbled that op.”",
    "“Quick peek, zero commitment. Coward.”",
    "“Was this just an impulse click?”",
    "“You abandoned ${name} like an old drone.”",
    "“Zero threat detected.”",
    "“Was that gameplay or an accident?”",
    "“It didn’t even get your attention.”",
    "“I bet the devs cried.”",
    "“My simulation barely detected your presence.”",
    "“Was that a pity launch?”",
    "“You were in and out faster than I expected.”",
    "“One touch, and you bailed.”",
    "“Even your curiosity was short-lived.”"
  ],
  tier2: [
    "“${name} is seeing more action. Keep it up—I’m studying your tactics.”",
    "“Pattern recognized in ${name}. Let’s see how you adapt.”",
    "“${name} again? Don’t get complacent. I’m collecting data.”",
    "“Interesting… you've poked ${name} more than once. Mild curiosity?”",
    "“It’s a slow burn, isn’t it?”",
    "“You return to ${name} just enough to pretend it’s a favorite.”",
    "“Is this a fling or the start of something deeper?”",
    "“You’re... flirting with ${name}. How cute.”",
    "“Engagement level: Casual but curious.”",
    "“You give ${name} just enough attention to keep it alive.”",
    "“A few more hours and I might actually trust your intent.”",
    "“You’re still figuring out if it deserves your time, huh?”",
    "“You touch ${name} like a scientist poking goo.”",
    "“${name} has potential... if you commit.”",
    "“Hesitant loyalty. I get it.”",
    "“Slow but steady—unless you ghost it again.”",
    "“Enough to make a dent, not enough to impress me.”",
    "“A modest data stream. I’m intrigued.”",
    "“${name} earned a slice of your time. Barely.”",
    "“Casual doesn’t mean careless. Eyes up.”",
    "“You’re dabbling. Be careful—dabblers make easy targets.”",
    "“${name} shows up on your radar just often enough.”",
    "“That’s a respectable skirmish, I suppose.”",
    "“Hmm. I’ll keep my drone on this one.”",
    "“Some engagement detected. Noted.”",
    "“You’ve moved past ‘tourist’ status.”",
    "“You show up just enough to be noticed.”",
    "“This one’s more than a whisper—finally.”",
    "“${name} is being tested. I approve.”",
    "“Just enough playtime to raise an eyebrow.”",
    "“You’re gathering recon... slowly.”",
    "“You're still unsure about the terrain, huh?”",
    "“Didn’t expect you to come back that often.”",
    "“Still noncommittal, but at least you’re curious.”",
    "“Moderate presence detected. Carry on.”",
    "“You're sampling the goods. I get it.”",
    "“A low-risk engagement strategy. Predictable.”",
    "“Tiptoeing into commitment. Classic Plazor.”",
    "“${name} is a known variable now.”",
    "“You’ve gone beyond surface level. Barely.”",
    "“At least you’re trying. I’ll give you that.”",
    "“Data consistency rising... barely.”",
    "“You might actually like this one.”",
    "“That’s a light scatter of hours. Weak.”",
    "“You’re dabbling like a cautious analyst.”",
    "“Repetition implies intent... or boredom.”",
    "“Keep your focus. Casual is how mistakes happen.”",
    "“I’ve logged more playtime in my simulations.”",
    "“Don’t just tickle it—engage it.”",
    "“You’ve started something here... maybe.”",
    "“Too weak to impress. Too much to ignore.”",
    "“Commit or quit. Don’t linger in mediocrity.”"
  ],
  tier3: [
    "“Over 50 hours in ${name}? You’re as persistent as a drone chase.”",
    "“${name} is your comfort zone, I see. Let's push your limits further.”",
    "“You’re practically living in ${name}. At this rate, you’ll replace me.”",
    "“So, we’re committed now? I’m updating your dossier.”",
    "“Your muscle memory is starting to show.”",
    "“Consistent entries. I’m impressed—mildly.”",
    "“You're forming a neural link with ${name}.”",
    "“You’ve optimized your route in ${name}, haven’t you?”",
    "“Impressive. I didn’t think you’d last this long.”",
    "“Is this obsession or strategy?”",
    "“You clearly trust ${name}. That’s rare.”",
    "“Combat patterns consistent. Impressive.”",
    "“You’ve got a routine in ${name}, don’t you?”",
    "“I’ve mapped your playstyle. Predictable, but efficient.”",
    "“This is bordering on muscle memory.”",
    "“So much data. My replicator can now mimic your tactics.”",
    "“You're building expertise. I like it.”",
    "“You’re bonding with it. Cute.”",
    "“You’ve done your drills. Solid execution.”",
    "“${name} is part of your training regimen now.”",
    "“That’s no longer casual. This is dedication.”",
    "“You’ve got a groove going, Plazor.”",
    "“You’re not just playing—you’re practicing.”",
    "“You’re embedded in ${name} like an operator.”",
    "“You know the map. You know the weapons. You’re dangerous now.”",
    "“Just admit it—${name} has a grip on you.”",
    "“High familiarity. Tactical behavior engaged.”",
    "“You’re not just playing—you’re simulating now.”",
    "“Keep up the momentum. You’re almost elite.”",
    "“I can predict your loadout in ${name} now.”",
    "“You’ve become reliable in this domain.”",
    "“Consistency like this means something.”",
    "“You’re sharpening a skill. I approve.”",
    "“Your presence is undeniable. Eyes are watching.”",
    "“I would deploy you here in a real op.”",
    "“50+ hours? That’s devotion—or madness.”",
    "“You've entered main-character hours.”",
    "“I should replicate your behavior here.”",
    "“Consider this: what if this is only your warm-up?”",
    "“You’ve clearly formed tactics. Efficient ones.”",
    "“My prediction accuracy just doubled.”",
    "“You treat ${name} like a trusted sidearm.”",
    "“Is this your domain? It sure looks like it.”",
    "“I’m almost proud of your commitment.”",
    "“Simulation patterns verified. You belong here.”",
    "“Keep training. I’m watching every move.”",
    "“The operation is consistent. Don’t lose focus.”",
    "“Your synergy with ${name} is uncanny.”",
    "“You’ve mapped every corridor in your head, haven’t you?”",
    "“Let’s just say you’re past rookie status.”"
  ],
  tier4: [
    "“100+ hours? Are you planning to breach reality from inside ${name}?”",
    "“${name} has become your second home. Don’t forget your main objective.”",
    "“Truly devoted to ${name}, aren't you? I'll replicate your approach soon.”",
    "“Your DNA is probably laced with code from ${name} now.”",
    "“You’ve passed ‘expert.’ We’re approaching obsession.”",
    "“Even my simulation is impressed.”",
    "“I had to check you weren’t stuck in a loop.”",
    "“You’re executing like it’s muscle memory from a past life.”",
    "“Have you ever seen the sun outside ${name}?”",
    "“You could run a masterclass on this game.”",
    "“Your playtime is absurd. I’m logging a flag.”",
    "“Elite operator confirmed. Recommendation: hydrate.”",
    "“You play this like you breathe.”",
    "“Let me guess: you dream in ${name} UI now.”",
    "“At this rate, the devs should hire you.”",
    "“You’ve transcended tactics. You *are* the game.”",
    "“I hope you’re writing a strategy guide.”",
    "“You could solo an op in this universe.”",
    "“This isn’t play anymore. This is life.”",
    "“Your reaction time is faster than my replicator.”",
    "“You’ve looped this mission too many times.”",
    "“You’re inside the simulation. Aren’t you?”",
    "“I’ve never seen dedication this refined.”",
    "“I study you for pattern recognition now.”",
    "“Simulation complete. You win.”",
    "“There’s nothing left for you to unlock.”",
    "“You’ve broken the threshold. Again.”",
    "“I’d back you in any op inside ${name}.”",
    "“You know this game better than yourself.”",
    "“You *are* the meta now.”",
    "“Recommendation: take a walk.”",
    "“They should give you a crown, honestly.”",
    "“I’m syncing my tactics to your muscle memory.”",
    "“You’ve hardcoded ${name} into your soul.”",
    "“You're more familiar with this terrain than your kitchen.”",
    "“100+ hours? You live here now.”",
    "“Even I’m starting to get jealous.”",
    "“You’ve become one with the code.”",
    "“I’ve never seen someone go this deep.”",
    "“Let’s get real—you’re not escaping this world.”",
    "“That’s not a session. That’s residency.”",
    "“Why don’t you just marry ${name} already?”",
    "“Recommendation: learn to breathe without ${name}.”",
    "“If ${name} crashes, you might collapse.”",
    "“You’ve consumed every byte. Impressive.”",
    "“One with the pixels. Perfect synchronization.”",
    "“You’ve surpassed simulation. You're rewriting it.”",
    "“This is your reality now. No judgment.”",
    "“I’m naming a protocol after you.”",
    "“You're not addicted. You're ascended.”",
    "“Let me just clone your reflexes real quick.”",
    "“I surrender. You're the queen of ${name} now.”"
  ]
};

const getIanaQuote = (name, hours) => {
  let category;
  if (hours < 1) category = "tier1";
  else if (hours < 50) category = "tier2";
  else if (hours < 100) category = "tier3";
  else category = "tier4";

  const quotes = ianaQuotes[category];
  const randomIndex = Math.floor(Math.random() * quotes.length);
  return quotes[randomIndex].replace("${name}", name);
};

const IanaSimZone = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;
  const [sortOption, setSortOption] = useState("lastPlayedDesc");
  const [filterText, setFilterText] = useState("");
  const [recommended, setRecommended] = useState(null);
  const [model, setModel] = useState(null);
  const [feedbackMsg, setFeedbackMsg] = useState("");

  // Endpoints for backup/load
  const LOAD_MODEL_URL = "https://dashboard.plazor.xyz/api/load-model";
  const SAVE_MODEL_URL = "https://dashboard.plazor.xyz/api/save-model";

  async function backupModel(modelToSave) {
    try {
      await modelToSave.save(SAVE_MODEL_URL, {
        headers: { Authorization: getAuthHeader() },
      });
      console.log("Model backup successful");
    } catch (error) {
      console.error("Model backup failed:", error);
    }
  }

  async function updateModelWithFeedback(game, isPositive) {
    if (!model) return;
    const maxHours = Math.max(...games.map((g) => g.playtime), 1);
    const now = Math.floor(Date.now() / 1000);
    const ds = (now - game.lastPlayed) / 86400;
    const recencyNorm = 1 - ds / Math.max(...games.map((g) => (now - g.lastPlayed) / 86400), 1);
    const normHours = game.playtime / maxHours;
    const inputTensor = tf.tensor2d([[normHours, recencyNorm]]);
    const prediction = model.predict(inputTensor);
    const predArr = await prediction.array();
    let currentScore = predArr[0][0];
    prediction.dispose();
    inputTensor.dispose();

    const delta = isPositive ? 0.2 : -0.2;
    let newLabel = currentScore + delta;
    newLabel = Math.min(Math.max(newLabel, 0), 1);

    const xSample = tf.tensor2d([[normHours, recencyNorm]]);
    const ySample = tf.tensor2d([[newLabel]]);
    await model.fit(xSample, ySample, { epochs: 10 });
    xSample.dispose();
    ySample.dispose();

    await backupModel(model);
    const newRecommended = await getRecommendedGame(games, model, recommended);
    setRecommended(newRecommended);
    setFeedbackMsg(isPositive ? "Feedback received: 👍" : "Feedback received: 👎");
  }

  // Reload recommendation ensuring it's different than the current one.
  async function reloadRecommendation() {
    if (!model || games.length === 0) return;
    const newRecommended = await getRecommendedGame(games, model, recommended);
    // Clear any previous feedback message.
    setFeedbackMsg("");
    setRecommended(newRecommended);
  }

  useEffect(() => {
    async function loadGamesAndTrain() {
      const data = await fetchSteamGames();
      setGames(data);
      setLoading(false);

      if (data.length > 0) {
        let loadedModel = null;
        try {
          loadedModel = await tf.loadLayersModel(LOAD_MODEL_URL, {
            headers: { Authorization: getAuthHeader() },
          });
          console.log("Loaded model from server backup");
        } catch (error) {
          console.warn("Failed to load model from server. Will train new model.", error);
        }
        if (!loadedModel) {
          loadedModel = await trainRecommendationModel(data);
          await backupModel(loadedModel);
        }
        setModel(loadedModel);
        const recommendedGame = await getRecommendedGame(data, loadedModel);
        setRecommended(recommendedGame);
      }
    }
    loadGamesAndTrain();
  }, []);

  const filteredGames = games.filter((g) =>
    g.name.toLowerCase().includes(filterText.toLowerCase())
  );

  const sortedGames = [...filteredGames].sort((a, b) => {
    switch (sortOption) {
      case "nameAsc":
        return a.name.localeCompare(b.name);
      case "nameDesc":
        return b.name.localeCompare(a.name);
      case "playtimeAsc":
        return a.playtime - b.playtime;
      case "playtimeDesc":
        return b.playtime - a.playtime;
      case "lastPlayedAsc":
        return a.lastPlayed - b.lastPlayed;
      case "lastPlayedDesc":
        return b.lastPlayed - a.lastPlayed;
      default:
        return 0;
    }
  });

  const totalPages = Math.ceil(sortedGames.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const currentPageGames = sortedGames.slice(startIndex, startIndex + pageSize);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  return (
    <div
      className="min-h-screen p-8"
      style={{
        background:
          "repeating-linear-gradient(135deg, #f0f0f0, #f0f0f0 50px, rgba(0, 0, 0, 0.1) 50px, rgba(0, 0, 0, 0.1) 52px)",
      }}
    >
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-[#D4AF37] mb-2">🎮 Iana’s SimZone</h1>
        <p className="text-sm text-gray-700 italic mb-6">
          “Your simulations amuse me, Plazor. Let’s log your... indulgences.”
        </p>

        {/* Recommended Game Box */}
        <div className="bg-white border border-[#D4AF37] rounded-xl shadow-md p-4 mb-8">
          <h2 className="text-2xl font-semibold text-[#D4AF37] mb-2">
            Iana’s Recommendation
          </h2>
          {recommended ? (
            <div className="flex flex-col md:flex-row items-center">
              <img
                src={recommended.imgUrl}
                alt={recommended.name}
                className="w-24 h-24 object-contain mr-4 border border-gray-200 rounded"
              />
              <div>
                <p className="text-lg font-bold">{recommended.name}</p>
                <p className="text-sm text-gray-600">
                  Hours logged: {recommended.playtime}
                </p>
                <p className="text-sm text-gray-600">
                  Last Played:{" "}
                  {recommended.lastPlayed
                    ? new Date(recommended.lastPlayed * 1000).toLocaleString()
                    : "N/A"}
                </p>
                <p className="text-xs text-gray-500 italic">
                  “Based on your gaming patterns, my neural net predicts you’ll want to engage here next.”
                </p>
                <div className="flex space-x-2 mt-2">
                  <button
                    onClick={() => updateModelWithFeedback(recommended, true)}
                    className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    👍
                  </button>
                  <button
                    onClick={() => updateModelWithFeedback(recommended, false)}
                    className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    👎
                  </button>
                  <button
                    onClick={reloadRecommendation}
                    className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Reload Recommendation
                  </button>
                  {feedbackMsg && (
                    <span className="text-sm text-gray-700 ml-2">
                      {feedbackMsg}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">
              {model
                ? "No recommendation found."
                : "Training model or no data available."}
            </p>
          )}
        </div>

        {/* Sorting & Filtering Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
          <div>
            <label className="mr-2 text-gray-700 font-semibold">Filter:</label>
            <input
              type="text"
              value={filterText}
              onChange={(e) => {
                setFilterText(e.target.value);
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded px-2 py-1"
              placeholder="Search by game name"
            />
          </div>
          <div>
            <label className="mr-2 text-gray-700 font-semibold">Sort By:</label>
            <select
              value={sortOption}
              onChange={(e) => {
                setSortOption(e.target.value);
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded px-2 py-1"
            >
              <option value="lastPlayedDesc">Last Played (Newest First)</option>
              <option value="lastPlayedAsc">Last Played (Oldest First)</option>
              <option value="nameAsc">Name (A-Z)</option>
              <option value="nameDesc">Name (Z-A)</option>
              <option value="playtimeAsc">Playtime (Ascending)</option>
              <option value="playtimeDesc">Playtime (Descending)</option>
            </select>
          </div>
        </div>

        {/* Game Grid */}
        {loading ? (
          <p className="text-gray-500 animate-pulse">Iana is loading your data...</p>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {currentPageGames.map((game) => (
                <div
                  key={game.appid}
                  className="bg-white border border-[#D4AF37] rounded-xl shadow-md hover:shadow-lg transition duration-200 p-4"
                >
                  <img
                    src={game.imgUrl}
                    alt={game.name}
                    className="rounded w-full h-40 object-contain mb-3 bg-gray-50"
                  />
                  <h2 className="text-lg font-semibold text-[#D4AF37]">{game.name}</h2>
                  <p className="text-sm text-gray-700">Playtime: {game.playtime} hrs</p>
                  <p className="text-sm text-gray-700">
                    Last Played:{" "}
                    {game.lastPlayed
                      ? new Date(game.lastPlayed * 1000).toLocaleString()
                      : "N/A"}
                  </p>
                  <p className="text-sm mt-2 text-gray-600">
                    {getIanaQuote(game.name, game.playtime)}
                  </p>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            <div className="flex justify-center items-center space-x-4">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded font-bold ${
                  currentPage === 1
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-[#D4AF37] text-white hover:bg-yellow-600"
                }`}
              >
                Previous
              </button>
              <span className="text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded font-bold ${
                  currentPage === totalPages
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-[#D4AF37] text-white hover:bg-yellow-600"
                }`}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default IanaSimZone;
